import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Loader2, RefreshCw, Eye, EyeOff, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { encryptJson, generatePassword } from "@/lib/secureCrypto";
import LinkShareOptions, { PasswordShareOptions } from "@/components/LinkShareOptions";

export default function EncryptedSendDialog({ note, open, onOpenChange }) {
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setPassword(generatePassword()); setResult(null); setShowPw(true); }
  }, [open]);

  const encrypt = async () => {
    const pw = password.trim();
    if (pw.length < 6) { toast.error("Use a password of at least 6 characters"); return; }
    setBusy(true);
    try {
      const sealed = await encryptJson({
        title: note.title || "", content: note.content || "",
        is_checklist: !!note.is_checklist, checklist_items: note.checklist_items || [],
      }, pw);
      const { data } = await api.post("/secret-notes", { ...sealed, frontend_origin: window.location.origin });
      setResult({ ...data, password: pw });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not encrypt the note");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="encrypted-send-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" /> Send encrypted
          </DialogTitle>
          <DialogDescription>
            The note is locked on this device before it leaves. Only someone with the password can read it — not even our server. The link lasts 7 days. Attachments aren't included.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <label className="font-mono-xs text-stone-500 block">Password</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" data-testid="encrypted-password-input" maxLength={128} autoComplete="new-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-full text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800" data-testid="encrypted-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}>
                  {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <Button variant="outline" size="icon" className="rounded-full shrink-0" onClick={() => setPassword(generatePassword())} data-testid="encrypted-password-generate" aria-label="New password">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={encrypt} disabled={busy} className="w-full rounded-full h-11" data-testid="encrypted-send-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Encrypt & get link
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="encrypted-result">
            <div>
              <div className="text-sm font-medium flex items-center gap-1.5"><Link2 className="h-4 w-4" />1. Send the link</div>
              <p className="text-xs text-muted-foreground">Email it, copy it, share it or show the QR code.</p>
            </div>
            <LinkShareOptions
              url={result.url}
              title="An encrypted note for you"
              testId="encrypted-link"
              onEmail={async (emails) => (await api.post(`/secret-notes/${result.token}/email`, { emails, frontend_origin: window.location.origin }, { headers: { "X-Manage-Key": result.manage_key } })).data}
            />
            <div className="text-sm font-medium pt-1">2. Send the password</div>
            <PasswordShareOptions password={result.password} testId="encrypted-pw" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
